import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { PolicyHeader } from "@/components/shared"
import { companyDetails } from "@/lib/company-details"

const effectiveDate = "April 12, 2026"

const sections = [
  {
    id: "digital-service",
    title: "Digital Nature of the Service",
    content:
      "This Refund Policy applies specifically to Monra eSIM and supplements Monra's Terms of Use and Monra eSIM Service Terms. Monra eSIM plans are digital products that are typically delivered immediately after purchase through a QR code, in-app installation, or other activation details. Delivery can happen right away, while plan activation generally occurs when the device first connects to a supported network in the purchased destination unless the product states otherwise. Because digital delivery begins immediately, purchases are generally final once the eSIM or its activation credentials have been delivered, installed, redeemed, activated, or used, except where applicable law requires otherwise or where Monra confirms a technical failure attributable to Monra.",
  },
  {
    id: "eligible-refunds",
    title: "When a Refund May Be Available",
    content:
      "Monra may approve a refund, replacement, or account credit after review in limited circumstances, including the following:",
    bullets: [
      "You were charged more than once for the same order or charged an incorrect amount.",
      "The eSIM could not be delivered or provisioned because of a verified technical failure on Monra's side or on the side of a Monra provisioning partner.",
      "Applicable law grants you a non-waivable refund right that overrides this policy.",
    ],
    extra:
      "Approval is not automatic. Monra may issue a replacement eSIM or account credit instead of a cash refund where that is commercially reasonable and permitted by law.",
  },
  {
    id: "non-refundable",
    title: "When a Refund Is Not Available",
    content:
      "To protect a digital eSIM business from abuse, Monra generally will not issue refunds in the following situations:",
    bullets: [
      "The QR code, activation details, or installation credentials were redeemed, viewed, installed, activated, or otherwise used.",
      "Any data usage occurred, including the first connection to a supported network in the purchased destination or any partial use of the purchased plan.",
      "The wrong country, region, or plan was selected, the purchase was accidental, or you changed your mind after checkout.",
      "Your device was not carrier-unlocked, was not eSIM-compatible, was outdated, or was otherwise unsupported or misconfigured.",
      "You experienced poor local coverage, low speeds, congestion, throttling, building interference, or service degradation caused by partner networks, local conditions, or factors outside Monra's control.",
      "You deleted the eSIM, lost the device, failed to follow installation steps, started travel later than planned, canceled the trip, or no longer need the plan.",
      "The purchase was made through an app store, reseller, marketplace, travel agency, or another third party that controls billing or refunds.",
    ],
  },
  {
    id: "request-process",
    title: "How to Request a Refund Review",
    content:
      "If you believe you qualify for a refund review, contact Monra support promptly and provide enough information for us to investigate.",
    orderedBullets: [
      "Email support@monralabs.com within 7 days of purchase or before the plan expires, whichever comes first.",
      "Include your order number, purchase date, account email, destination, device model, and a clear description of the problem.",
      "Attach any supporting screenshots or error messages that help explain the issue.",
      "Do not delete the eSIM or reset the device until support confirms whether additional technical review is needed.",
    ],
  },
  {
    id: "review-and-evidence",
    title: "Review, Evidence, and Abuse Prevention",
    content:
      "Monra may review activation logs, provisioning records, usage records, billing records, network status information, and device screenshots to determine whether a refund request is valid. We may request additional evidence before making a decision. Refund abuse, false statements, manipulated screenshots, repeated refund claims, unjustified chargebacks, or attempts to obtain service without payment may result in request denial, account suspension, or permanent loss of access to Monra services.",
  },
  {
    id: "third-party",
    title: "Third-Party Purchases and Chargebacks",
    content:
      "If you purchased through a third-party marketplace, app store, or reseller, that third party may control billing and refunds. In those cases, you must follow the refund process of the original seller. If you initiate a chargeback or payment dispute without first giving Monra a reasonable opportunity to review the issue, Monra may suspend your account, block future purchases, or contest the dispute using its transaction and activation records.",
  },
  {
    id: "timing",
    title: "Refund Method and Timing",
    content:
      "If a refund is approved, Monra will generally send the refund back to the original payment method unless another method is required by law or reasonably necessary. Timing depends on your payment provider and financial institution. Approved refunds typically appear within 5 to 10 business days after processing, but delays can occur. Non-refundable taxes, duties, currency conversion costs, or third-party processing fees may be excluded from refunds where permitted by law.",
  },
  {
    id: "contact",
    title: "Contact Us",
    content:
      "For refund questions or to request a refund review, contact Monra support.",
    email: companyDetails.contactEmail,
  },
]

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-background animate-fade-in">
      <Navbar links={[]} logoVariant="main" logoBehavior="navigate" isTransparent={false} />

      <PolicyHeader
        title="Refund Policy"
        subtitle="Refund rules for Monra eSIM plans and digital delivery."
        variant="esim"
        effectiveDate={effectiveDate}
      />

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="legal-card mb-10">
            <h2 className="mb-4 font-sans text-lg font-semibold text-foreground">Table of Contents</h2>
            <ol className="space-y-2 text-sm text-muted-foreground">
              {sections.map((section, index) => (
                <li key={section.id} className="flex gap-3">
                  <span className="w-6 shrink-0 tabular-nums text-foreground/45">{String(index + 1).padStart(2, "0")}</span>
                  <a href={`#${section.id}`} className="transition-colors hover:text-foreground">
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-20 border-t border-border py-10">
              <h2 className="mb-3 font-sans text-xl font-semibold tracking-tight text-foreground">{section.title}</h2>
              <p className="leading-relaxed text-muted-foreground">{section.content}</p>
              {"bullets" in section && section.bullets && (
                <ul className="mt-4 space-y-2 text-muted-foreground">
                  {section.bullets.map((bullet, index) => (
                    <li key={index} className="list-disc pl-5">
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
              {"orderedBullets" in section && section.orderedBullets && (
                <ol className="mt-4 list-decimal space-y-2 pl-5 text-muted-foreground">
                  {section.orderedBullets.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ol>
              )}
              {"extra" in section && section.extra && (
                <p className="mt-4 leading-relaxed text-muted-foreground">{section.extra}</p>
              )}
              {"email" in section && section.email && (
                <a
                  href={`mailto:${section.email}`}
                  className="mt-2 inline-block font-semibold text-esim-deep transition-colors hover:underline"
                >
                  {section.email}
                </a>
              )}
            </section>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  )
}
